import { useState, useCallback } from 'react';
import { usersApi } from '../api/users';
import { useAuthStore } from '../stores/authStore';
import { useImagePicker } from './useImagePicker';

interface UseProfilePictureReturn {
  isUploading: boolean;
  pickAndUploadImage: () => Promise<void>;
  deleteImage: () => Promise<void>;
}

export function useProfilePicture(): UseProfilePictureReturn {
  const [isUploading, setIsUploading] = useState(false);
  const updateUser = useAuthStore((s) => s.updateUser);
  const user = useAuthStore((s) => s.user);
  const { pickAndPrepareImage } = useImagePicker();

  const pickAndUploadImage = useCallback(async () => {
    try {
      // Pick first — don't show the upload spinner while the picker is open
      // (or if the user cancels, which would otherwise leave it spinning).
      const result = await pickAndPrepareImage();
      if (!result) {
        return;
      }

      setIsUploading(true);

      // Upload to backend
      const updatedUser = await usersApi.uploadProfilePicture(result.formData);

      // Update local user state. Always stamp a FRESH client timestamp for
      // cache-busting: the stored image path is deterministic, so if the
      // server's updated_at didn't change (or matched to the second) the image
      // would keep showing the cached version.
      if (user && updatedUser) {
        updateUser({
          profile_picture_url: updatedUser.profile_picture_url,
          updated_at: new Date().toISOString(),
        });
      }
    } catch (error) {
      console.error('Failed to upload profile picture:', error);
      throw error;
    } finally {
      setIsUploading(false);
    }
  }, [pickAndPrepareImage, updateUser, user]);

  const deleteImage = useCallback(async () => {
    try {
      setIsUploading(true);
      const updatedUser = await usersApi.deleteProfilePicture();

      if (user && updatedUser) {
        updateUser({
          profile_picture_url: null,
          updated_at: updatedUser.updated_at || new Date().toISOString(),
        });
      }
    } catch (error) {
      console.error('Failed to delete profile picture:', error);
      throw error;
    } finally {
      setIsUploading(false);
    }
  }, [updateUser, user]);

  return {
    isUploading,
    pickAndUploadImage,
    deleteImage,
  };
}
